import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Octokit } from 'octokit';

export interface GithubCredentialUiOptions {
  requestUserCredentials?: {
    secretsKey: string;
    additionalScopes?: string[];
  };
}

/**
 * A GitHub login component that is used by the Scaffolder for entity creation via templates.
 * We probably don't need to use the individual user, I'm guessing it's for auditing purposes.
 * @param uiSchema
 * @constructor
 */
export const GithubLogin = ({
  uiSchema,
}: FieldExtensionComponentProps<string, GithubCredentialUiOptions>) => {
  const scmAuthApi = useApi(scmAuthApiRef);
  const { setSecrets } = useTemplateSecrets();

  const [username, setUsername] = useState('');

  const doLogin = useCallback(async () => {
    const { requestUserCredentials } = uiSchema?.['ui:options'] ?? {};

    if (!requestUserCredentials) {
      return;
    }

    // credit/inspiration: https://github.com/backstage/backstage/blob/5330e3758888b46da7833cdd2ae8efd39797044e/plugins/scaffolder/src/components/fields/RepoUrlPicker/RepoUrlPicker.tsx#L129
    // user has requested that we use the users credentials
    // so lets grab them using the scmAuthApi and pass through
    // any additional scopes from the ui:options
    const { token } = await scmAuthApi.getCredentials({
      url: `https://github.com/meltwater`,
      additionalScope: {
        repoWrite: true,
        customScopes: { github: requestUserCredentials.additionalScopes },
      },
    });

    const octokit = new Octokit({ auth: token });

    const response = await octokit.rest.users.getAuthenticated();
    if (response.status === 200) {
      setUsername(response.data.login);

      // set the secret using the key provided in the the ui:options for use
      // in the templating the manifest with ${{ secrets[secretsKey] }}
      setSecrets({ [requestUserCredentials.secretsKey]: token });
    }
  }, [scmAuthApi, setSecrets, uiSchema]);

  useEffect(() => {
    doLogin();
  }, [doLogin]);

  return (
    <div>
      {username.length > 0 ? (
        <span>
          You are logged into GitHub as <b>{username}</b>
        </span>
      ) : (
        <span>
          <b>Something went wrong when verifying your GitHub credentials!</b>
        </span>
      )}
    </div>
  );
};
